import { useState, useEffect } from 'react';
import { __ } from '@wordpress/i18n';
import { apiFetch } from '../scripts/api';

export default function ActivityRegistrationsPanel( { activityId } ) {
	const [ state, setState ] = useState( {
		loading: true,
		error: null,
		registrations: [],
		statusTerms: [],
	} );
	const [ saving, setSaving ] = useState( {} ); // { [registrationId]: 'saving'|'saved'|'error' }
	const [ draftStatus, setDraftStatus ] = useState( {} ); // { [registrationId]: statusId }

	useEffect( () => {
		apiFetch( `/anacrm/v1/activity/${ activityId }/registrations` )
			.then( ( data ) => {
				const draft = {};
				data.registrations.forEach( ( r ) => {
					draft[ r.id ] = r.statusId ?? '';
				} );
				setDraftStatus( draft );
				setState( { loading: false, error: null, ...data } );
			} )
			.catch( ( err ) =>
				setState( {
					loading: false,
					error: err.message,
					registrations: [],
					statusTerms: [],
				} )
			);
	}, [ activityId ] );

	async function handleSave( regId ) {
		const statusId = draftStatus[ regId ];
		setSaving( ( s ) => ( { ...s, [ regId ]: 'saving' } ) );
		try {
			await apiFetch( `/anacrm/v1/registrations/${ regId }`, {
				method: 'PATCH',
				body: JSON.stringify( { status_id: statusId } ),
			} );
			setState( ( prev ) => ( {
				...prev,
				registrations: prev.registrations.map( ( r ) =>
					r.id === regId
						? {
								...r,
								statusId,
								statusName:
									prev.statusTerms.find(
										( t ) => t.id === statusId
									)?.name ?? '',
						  }
						: r
				),
			} ) );
			setSaving( ( s ) => ( { ...s, [ regId ]: 'saved' } ) );
			setTimeout(
				() =>
					setSaving( ( s ) => {
						const n = { ...s };
						delete n[ regId ];
						return n;
					} ),
				2000
			);
		} catch ( err ) {
			setSaving( ( s ) => ( { ...s, [ regId ]: 'error' } ) );
		}
	}

	if ( state.loading ) {
		return (
			<div className="px-6 py-4 text-sm text-gray animate-pulse">
				{ __( 'Loading registrations…', 'anacrm' ) }
			</div>
		);
	}

	if ( state.error ) {
		return (
			<div className="px-6 py-3 border-l-4 border-red text-red text-sm">
				{ state.error }
			</div>
		);
	}

	if ( state.registrations.length === 0 ) {
		return (
			<div className="px-6 py-4 text-sm text-gray">
				{ __( 'No registrations yet.', 'anacrm' ) }
			</div>
		);
	}

	return (
		<div className="bg-gray-light/50 border-l-4 border-green-dark px-6 py-4">
			<h3 className="text-sm font-semibold mb-3">
				{ __( 'Registrations', 'anacrm' ) }
			</h3>
			<table className="w-full text-sm border-collapse">
				<thead>
					<tr>
						<th className="text-left px-2 py-1 font-semibold border-b border-green-dark">
							{ __( 'Person', 'anacrm' ) }
						</th>
						<th className="text-left px-2 py-1 font-semibold border-b border-green-dark">
							{ __( 'Status', 'anacrm' ) }
						</th>
						<th className="px-2 py-1 border-b border-green-dark"></th>
					</tr>
				</thead>
				<tbody>
					{ state.registrations.map( ( reg ) => (
						<tr
							key={ reg.id }
							className="border-t border-green-dark/30"
						>
							<td className="px-2 py-2">{ reg.personName }</td>
							<td className="px-2 py-2">
								<select
									value={ draftStatus[ reg.id ] ?? '' }
									onChange={ ( e ) =>
										setDraftStatus( ( d ) => ( {
											...d,
											[ reg.id ]: Number(
												e.target.value
											),
										} ) )
									}
									className="border-2 border-green-dark px-2 py-1 text-sm bg-white"
								>
									{ ! draftStatus[ reg.id ] && (
										<option value="">—</option>
									) }
									{ state.statusTerms.map( ( term ) => (
										<option
											key={ term.id }
											value={ term.id }
										>
											{ term.name }
										</option>
									) ) }
								</select>
							</td>
							<td className="px-2 py-2 whitespace-nowrap">
								<button
									onClick={ () => handleSave( reg.id ) }
									disabled={ saving[ reg.id ] === 'saving' }
									className="px-3 py-1 text-sm border-2 border-green-dark bg-white hover:bg-yellow disabled:opacity-50 cursor-pointer"
								>
									{ {
										saving: __( 'Saving…', 'anacrm' ),
										saved: __( 'Saved ✓', 'anacrm' ),
										error: __( 'Error ✗', 'anacrm' ),
									}[ saving[ reg.id ] ] ??
										__( 'Save', 'anacrm' ) }
								</button>
							</td>
						</tr>
					) ) }
				</tbody>
			</table>
		</div>
	);
}
